import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { type FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { AuthenticatedAppBoundary } from '@bbkAdminComponents/app-layout/authenticated-app-boundary';
import { AdminRouteSuspenseLoader } from '@bbkAdminComponents/loaders/admin-route-suspense-loader';
import { LogInRouter } from '@bbkAdminComponents/log-in-flow/log-in.router';
import { useAppInit, useSyncActiveContext } from '@bbkAdminRedux/app/hooks';
import { selectCompany, selectUser } from '@bbkAdminRedux/app/selectors';
import { isUnauthorizedError } from '@bbkAdminRedux/rtkq/auth-status';
import { selectAuthStatus } from '@bbkAdminRedux/rtkq/auth-utils';
import {
  currentUserSlice,
  useIdentifyUser,
} from '@bbkAdminRedux/rtkq/current-user.slice';
import { useQueryFeatureFlags } from '@bbkAdminRedux/rtkq/feature-flags.slice';
import { useUpgradeSession } from '@bbkAdminRedux/rtkq/use-upgrade-session';
import { NoApps } from '@bbkAdminRouter/no-apps.page';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';

import '@bbkAdminStylesheets/app-base.scss';

// Keep product routes and their dependencies out of the anonymous sign-in download.
// React only invokes this import when the authenticated branch is rendered.
const AppRoot = React.lazy(() =>
  import(
    /* webpackChunkName: "authenticated-app" */
    '@bbkAdminComponents/app-layout/app-root'
  ).then((module) => ({ default: module.AppRoot }))
);

export const AuthRoot: React.FC = () => {
  useUpgradeSession();
  useIdentifyUser();
  const user = useSelector(selectUser);
  const company = useSelector(selectCompany);
  const { isLoading: isFFLoading } = useQueryFeatureFlags();

  const [fetchUser$] = currentUserSlice.endpoints.getCurrentUser.useLazyQuery();
  const appInit = useAppInit();
  const needsSync = useSyncActiveContext(user);

  // Signed-in state is a single derived value from the /api/current_user probe
  // (the server's authority on the session), not a JS-visible JWT cookie and
  // not a component-local copy that can drift. Until it resolves we render the
  // loader instead of flashing the sign-in page.
  const authStatus = useSelector(selectAuthStatus);

  useEffect(() => {
    // Force a fresh probe on mount (no preferCacheValue): a retained fulfilled
    // entry must not stand in for a live session after the app remounts.
    //
    // No success handler: `useSyncActiveContext` is the single initializer for
    // an authenticated session. The `catch` still calls `appInit`, since the
    // stale-session bounce has no user for that hook to act on.
    fetchUser$(undefined, false)
      .unwrap()
      .catch((err: FetchBaseQueryError) => {
        if (isUnauthorizedError(err)) {
          appInit(undefined);
        } else {
          // Transient probe failure (network / 5xx): leave any previously
          // established session intact (the selector retains it) and just log.
          GlobalLogger.error(err);
        }
      });
  }, [appInit, fetchUser$]);

  // `needsSync` covers the "authenticated but uninitialized" window: the first
  // commit after the probe resolves, and any later commit where the location
  // has moved to a tenant the stored context has not caught up with. Holding
  // the loader for that commit keeps pages from rendering, and firing
  // app-scoped requests, against the previous tenant.
  if (isFFLoading || authStatus === 'loading' || needsSync) {
    return (
      <div className="tw-h-screen">
        <AdminRouteSuspenseLoader message=" " />
      </div>
    );
  }

  if (authStatus === 'unauthenticated') {
    return <LogInRouter />;
  }

  if (company && company.applications.length === 0) {
    return <NoApps company={company} />;
  }

  if (!user) {
    return null;
  }

  return (
    <div className="bbk-authRoot tw-box-content">
      <AuthenticatedAppBoundary>
        <AppRoot />
      </AuthenticatedAppBoundary>
    </div>
  );
};
